import React, { useState, useCallback, useMemo, useRef } from 'react';
import { saveAs } from 'file-saver';
import {
  CheckCircle2, AlertTriangle, AlertOctagon, X, Shield, Download, Copy,
  Zap, RefreshCw, FileText, Info, ChevronDown, ChevronRight, Search,
  Wrench, ShieldOff, ShieldCheck, Bug, Sparkles, Eye, EyeOff,
  BarChart3, PieChart, TrendingUp, Target, Clock, Database, Filter,
  ArrowUp, ArrowDown, Minimize2, Maximize2, List, GitMerge, Code, FileCode,
  AlertCircle, Check, FileJson, FileSpreadsheet, Upload, Settings
} from 'lucide-react';

// ============================================
// THEME (matching main app)
// ============================================
const T = {
  bg:'#0a0e17',s:'#111827',s2:'#1a2235',s3:'#1e293b',b:'#1e293b',
  t:'#e2e8f0',t2:'#94a3b8',t3:'#64748b',p:'#3b82f6',pl:'#60a5fa',
  g:'#10b981',gl:'#34d399',w:'#f59e0b',wl:'#fbbf24',d:'#ef4444',dl:'#f87171',
  pp:'#8b5cf6',ppl:'#a78bfa',te:'#14b8a6',pk:'#ec4899',cy:'#06b6d4',or:'#f97316'
};

const S = {
  container:{background:T.bg,color:T.t,fontFamily:"'Inter','Segoe UI',system-ui,sans-serif",padding:'12px',borderRadius:'8px',border:`1px solid ${T.b}`},
  header:{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:'10px',paddingBottom:'8px',borderBottom:`1px solid ${T.b}`},
  title:{fontSize:'0.95rem',fontWeight:700,display:'flex',alignItems:'center',gap:'6px'},
  btn:{padding:'5px 10px',borderRadius:'4px',border:'none',cursor:'pointer',fontSize:'0.74rem',fontWeight:600,display:'flex',alignItems:'center',gap:'4px',transition:'all 0.2s',whiteSpace:'nowrap'},
  bPri:{background:`linear-gradient(135deg,${T.p},#2563eb)`,color:'white'},
  bSuc:{background:`linear-gradient(135deg,${T.g},#059669)`,color:'white'},
  bWar:{background:`linear-gradient(135deg,${T.w},#d97706)`,color:'white'},
  bDan:{background:`linear-gradient(135deg,${T.d},#dc2626)`,color:'white'},
  bGho:{background:'transparent',color:T.t2,border:`1px solid ${T.b}`},
  bPP:{background:`linear-gradient(135deg,${T.pp},#7c3aed)`,color:'white'},
  card:{background:T.s2,border:`1px solid ${T.b}`,borderRadius:'6px',padding:'8px',marginBottom:'6px'},
  statCard:{background:T.s2,border:`1px solid ${T.b}`,borderRadius:'5px',padding:'8px',textAlign:'center'},
  badge:(c)=>({display:'inline-block',padding:'2px 6px',borderRadius:'3px',fontSize:'0.58rem',fontWeight:600,background:`${c}22`,color:c}),
  progressBar:{height:'6px',background:T.b,borderRadius:'3px',overflow:'hidden'},
  progressFill:{height:'100%',borderRadius:'3px',transition:'width 0.5s'},
  tag:{display:'inline-flex',alignItems:'center',gap:'3px',padding:'2px 6px',borderRadius:'3px',fontSize:'0.6rem',fontWeight:600},
  codeBlock:{background:'#0d1117',color:'#c9d1d9',padding:'10px',borderRadius:'5px',fontSize:'0.62rem',overflowX:'auto',maxHeight:'300px',overflowY:'auto',border:`1px solid ${T.b}`,lineHeight:'1.3',fontFamily:'monospace'},
  issueRow:{display:'flex',alignItems:'flex-start',gap:'6px',padding:'6px 8px',borderRadius:'4px',marginBottom:'3px',fontSize:'0.68rem',cursor:'pointer',transition:'all 0.15s'},
  tooltip:{position:'absolute',background:T.s3,border:`1px solid ${T.b}`,borderRadius:'5px',padding:'6px 8px',fontSize:'0.62rem',color:T.t,zIndex:100,boxShadow:'0 4px 12px rgba(0,0,0,0.5)',maxWidth:'350px'},
  grid:(n)=>({display:'grid',gridTemplateColumns:`repeat(${n},1fr)`,gap:'4px'}),
  modal:{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.85)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:300},
  modalC:{background:T.s2,borderRadius:'10px',padding:'12px',maxWidth:'98vw',maxHeight:'96vh',overflow:'auto',border:`1px solid ${T.b}`,boxShadow:'0 10px 30px rgba(0,0,0,0.5)'}
};

// ============================================
// COMPLETE PLMXML VALIDATOR ENGINE
// ============================================
class PLMXMLValidator {
  constructor(xmlContent) {
    this.originalXml = xmlContent;
    this.xml = xmlContent;
    this.errors = [];
    this.warnings = [];
    this.suggestions = [];
    this.fixes = [];
    this.stats = { 
      ebom: 0, mbom: 0, bop: 0, boe: 0, wp: 0, 
      tools: 0, skills: 0, quality: 0, ewi: 0, 
      relations: 0, total: 0, operations: 0, fgs: 0,
      processes: 0, equipment: 0, documents: 0
    };
    this.validated = false;
    this.correctionsApplied = [];
  }

  validate() {
    this.errors = [];
    this.warnings = [];
    this.suggestions = [];
    this.fixes = [];
    this.validated = true;

    // 1. XML Structure Checks
    this._checkXMLWellFormed();
    if (this.errors.length > 0 && this.errors.some(e => e.priority === 'CRITICAL')) {
      return this;
    }

    // 2. Schema & Namespace Checks
    this._checkPLMXMSchema();
    
    // 3. Required Elements
    this._checkRequiredElements();
    
    // 4. Business Object Checks
    this._checkBusinessObjects();
    
    // 5. Relation Checks
    this._checkRelations();
    
    // 6. Work Package Checks
    this._checkWorkPackage();
    
    // 7. BOP Structure Checks
    this._checkBOPStructure();
    
    // 8. BOE Hierarchy Checks
    this._checkBOEHierarchy();
    
    // 9. ID Uniqueness Checks
    this._checkIDsAndUniqueness();
    
    // 10. Attribute Checks
    this._checkAttributes();
    
    // 11. BMIDE Type Checks
    this._checkBMIDETypes();
    
    // 12. Forbidden Patterns
    this._checkForbiddenPatterns();
    
    // 13. Generate Statistics
    this._generateStats();
    
    // 14. Generate Suggestions
    this._generateSuggestions();
    
    // 15. Teamcenter 2512 Specific Checks
    this._checkTeamcenter2512Compatibility();
    
    return this;
  }

  // ============================================
  // 1. XML WELL-FORMED CHECK
  // ============================================
  _checkXMLWellFormed() {
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(this.xml, 'text/xml');
      const parseError = doc.querySelector('parsererror');
      if (parseError) {
        this.errors.push({
          id: 'XML-001',
          priority: 'CRITICAL',
          category: 'XML Structure',
          title: 'Malformed XML',
          detail: parseError.textContent || 'XML parser error',
          impact: 'Import will FAIL - XML parser cannot read the file.',
          fix: 'Check for unclosed tags, invalid characters, or encoding issues.',
          autoFix: false
        });
        return;
      }
      // Check for valid root
      if (!doc.documentElement || doc.documentElement.tagName !== 'PLMXML') {
        this.errors.push({
          id: 'XML-002',
          priority: 'CRITICAL',
          category: 'XML Structure',
          title: 'Invalid Root Element',
          detail: `Root element is "${doc.documentElement?.tagName || 'undefined'}", expected "PLMXML"`,
          impact: 'Import will FAIL - Teamcenter expects PLMXML root.',
          fix: 'Change root element to <PLMXML> with proper namespace.',
          autoFix: true,
          autoFixFn: () => { this.xml = this.xml.replace(/^<\w+/, '<PLMXML'); }
        });
      }
    } catch (e) {
      this.errors.push({
        id: 'XML-003',
        priority: 'CRITICAL',
        category: 'XML Structure',
        title: 'XML Parse Error',
        detail: e.message,
        impact: 'Import will FAIL.',
        fix: 'Verify the file is valid XML and not empty.',
        autoFix: false
      });
    }
  }

  // ============================================
  // 2. SCHEMA & NAMESPACE CHECKS
  // ============================================
  _checkPLMXMSchema() {
    // Check PLMXML namespace
    if (!this.xml.includes('PLMXMLSchema')) {
      this.errors.push({
        id: 'SCH-001',
        priority: 'CRITICAL',
        category: 'Schema',
        title: 'Missing PLMXML Schema',
        detail: 'The file does not contain the required PLMXML schema declaration.',
        impact: 'Import will FAIL - Teamcenter cannot recognize this as PLMXML.',
        fix: 'Add xmlns="http://www.plmxml.org/Schemas/PLMXMLSchema" to the PLMXML root element.',
        autoFix: true,
        autoFixFn: () => {
          this.xml = this.xml.replace(/<PLMXML/, '<PLMXML xmlns="http://www.plmxml.org/Schemas/PLMXMLSchema"');
        }
      });
    }

    // Check schemaVersion
    const versionMatch = this.xml.match(/schemaVersion="(\d+)"/);
    if (!versionMatch) {
      this.warnings.push({
        id: 'SCH-002',
        priority: 'HIGH',
        category: 'Schema',
        title: 'Missing Schema Version',
        detail: 'No schemaVersion attribute found. Teamcenter 2512 requires schemaVersion="6".',
        impact: 'May cause compatibility issues during import.',
        fix: 'Add schemaVersion="6" to PLMXML root element.',
        autoFix: true,
        autoFixFn: () => {
          this.xml = this.xml.replace(/<PLMXML([^>]*)>/, (match, attrs) => {
            if (!attrs.includes('schemaVersion=')) {
              return `<PLMXML schemaVersion="6"${attrs ? ' ' + attrs : ''}>`;
            }
            return match;
          });
        }
      });
    } else if (parseInt(versionMatch[1]) < 6) {
      this.warnings.push({
        id: 'SCH-003',
        priority: 'HIGH',
        category: 'Schema',
        title: `Schema Version ${versionMatch[1]} - Teamcenter 2512 requires version 6`,
        detail: 'Using older schema version may cause import issues.',
        impact: 'Some features may not work correctly.',
        fix: 'Upgrade to schemaVersion="6"',
        autoFix: true,
        autoFixFn: () => {
          this.xml = this.xml.replace(/schemaVersion="\d+"/, 'schemaVersion="6"');
        }
      });
    }

    // Check Header
    if (!this.xml.includes('<Header')) {
      this.errors.push({
        id: 'SCH-004',
        priority: 'CRITICAL',
        category: 'Schema',
        title: 'Missing Header Element',
        detail: 'PLMXML requires a Header element with transferContext.',
        impact: 'Import will FAIL.',
        fix: 'Add <Header> element with transferContext="incremental_import".',
        autoFix: true,
        autoFixFn: () => {
          const today = new Date().toISOString().split('T')[0];
          const header = `\n <Header id="HDR-AUTO" traverseRootRefs="#PV-EBOM #PV-MBOM #PV-BOP" transferContext="incremental_import">\n  <Description>Validated PLMXML Import | ${today}</Description>\n </Header>`;
          this.xml = this.xml.replace(/<PLMXML[^>]*>/, `$&\n${header}`);
        }
      });
    }

    // Check Site
    if (!this.xml.includes('<Site')) {
      this.warnings.push({
        id: 'SCH-005',
        priority: 'MEDIUM',
        category: 'Schema',
        title: 'Missing Site Element',
        detail: 'No Site element found. Teamcenter requires a Site context for import.',
        impact: 'Objects may not be assigned to a site properly.',
        fix: 'Add <Site> element with valid siteId.',
        autoFix: true,
        autoFixFn: () => {
          const site = `\n <Site id="SITE-AUTO" name="IMC--1629097399" siteId="-1629097399"><ApplicationRef application="Teamcenter" label="gOIAAAAEp71LUC" version="gOIAAAAEp71LUC"/><UserData id="UD-S-AUTO"><UserValue title="dbms" value="1"/></UserData></Site>\n <AccessIntent id="A-AUTO" ownerRefs="#SITE-AUTO" intent="reference"/>`;
          this.xml = this.xml.replace(/<PLMXML[^>]*>/, `$&\n${site}`);
        }
      });
    }
  }

  // ============================================
  // 3. REQUIRED ELEMENTS
  // ============================================
  _checkRequiredElements() {
    // Check for any product objects
    const hasProducts = this.xml.includes('<Product') || this.xml.includes('<Process');
    if (!hasProducts) {
      this.errors.push({
        id: 'REQ-001',
        priority: 'CRITICAL',
        category: 'Required Elements',
        title: 'No Business Objects Found',
        detail: 'The file contains no Product, Process, or Operation elements.',
        impact: 'Import will create no objects.',
        fix: 'Add Product, Process, or Operation elements to the file.',
        autoFix: false
      });
    }

    // Check for revisions
    const hasRevisions = this.xml.includes('<ProductRevision') || this.xml.includes('<ProcessRevision') || this.xml.includes('<OperationRevision');
    const hasMasters = this.xml.includes('<Product') || this.xml.includes('<Process') || this.xml.includes('<Operation');
    
    if (hasRevisions && !hasMasters) {
      this.errors.push({
        id: 'REQ-002',
        priority: 'CRITICAL',
        category: 'Required Elements',
        title: 'Revisions without Masters',
        detail: 'Found Revision elements but no corresponding Master elements.',
        impact: 'Import will FAIL - "No Item or Product Identifier" (203318).',
        fix: 'Each Revision must have a corresponding Master element.',
        autoFix: false
      });
    }
  }

  // ============================================
  // 4. BUSINESS OBJECT CHECKS
  // ============================================
  _checkBusinessObjects() {
    // Count Products
    const products = (this.xml.match(/<Product\s/g) || []).length;
    const productRevisions = (this.xml.match(/<ProductRevision\s/g) || []).length;
    const operations = (this.xml.match(/<Operation\s/g) || []).length;
    const processes = (this.xml.match(/<Process\s/g) || []).length;
    
    // Check Operation types
    if (operations > 0 && !this.xml.includes('subType="MEOP"') && !this.xml.includes('subType="MESOperation"')) {
      this.warnings.push({
        id: 'BO-001',
        priority: 'HIGH',
        category: 'Business Objects',
        title: 'Operation without proper subType',
        detail: 'Operation elements should use subType="MEOP" or "MESOperation" for Teamcenter 2512.',
        impact: 'May cause type mismatch errors (39014) during import.',
        fix: 'Add subType="MEOP" to all Operation elements.',
        autoFix: true,
        autoFixFn: () => {
          this.xml = this.xml.replace(/<Operation\s+id="([^"]*)"\s+name="([^"]*)"(?:\s+subType="([^"]*)")?/g, 
            (match, id, name, subType) => {
              if (!subType || (subType !== 'MEOP' && subType !== 'MESOperation')) {
                return `<Operation id="${id}" name="${name}" subType="MEOP"`;
              }
              return match;
            });
        }
      });
    }

    // Check Process types
    if (processes > 0 && !this.xml.includes('subType="Mbc0FuncGroup"') && !this.xml.includes('subType="MESFuncGroup"')) {
      this.warnings.push({
        id: 'BO-002',
        priority: 'HIGH',
        category: 'Business Objects',
        title: 'Process without proper subType',
        detail: 'Process/FuncGroup elements should use subType="Mbc0FuncGroup" or "MESFuncGroup".',
        impact: 'Functional Groups may not create correctly.',
        fix: 'Add subType="Mbc0FuncGroup" to all Process elements.',
        autoFix: true,
        autoFixFn: () => {
          this.xml = this.xml.replace(/<Process\s+id="([^"]*)"\s+name="([^"]*)"(?:\s+subType="([^"]*)")?/g,
            (match, id, name, subType) => {
              if (!subType || (subType !== 'Mbc0FuncGroup' && subType !== 'MESFuncGroup')) {
                return `<Process id="${id}" name="${name}" subType="Mbc0FuncGroup"`;
              }
              return match;
            });
        }
      });
    }

    // Check MBOM types - Teamcenter 2512 specific
    if (this.xml.includes('subType="Mfg0CollabMfgPrd"')) {
      this.errors.push({
        id: 'BO-003',
        priority: 'CRITICAL',
        category: 'Business Objects',
        title: 'Legacy MBOM Type Detected',
        detail: 'Mfg0CollabMfgPrd is a legacy type. Use ARA4MPart or MESPart for Teamcenter 2512.',
        impact: 'Import will FAIL with "Part Revision is not a valid Item Revision Type" (48067).',
        fix: 'Replace Mfg0CollabMfgPrd with ARA4MPart or MESPart.',
        autoFix: true,
        autoFixFn: () => {
          this.xml = this.xml.replace(/subType="Mfg0CollabMfgPrd"/g, 'subType="ARA4MPart"');
          this.xml = this.xml.replace(/subType="Mfg0CollabMfgPrdRevision"/g, 'subType="ARA4MPartRevision"');
        }
      });
    }

    // Check for missing UserData on revisions
    const revMatches = this.xml.match(/<ProductRevision[^>]*>/g) || [];
    revMatches.forEach(rev => {
      if (!rev.includes('<UserData') && !rev.includes('/>')) {
        this.warnings.push({
          id: 'BO-004',
          priority: 'MEDIUM',
          category: 'Business Objects',
          title: 'Revision without UserData',
          detail: 'ProductRevision found without UserData element.',
          impact: 'May cause missing last_mod_date attribute.',
          fix: 'Add UserData with last_mod_date to each Revision.',
          autoFix: true,
          autoFixFn: () => {
            const today = new Date().toISOString().split('T')[0];
            this.xml = this.xml.replace(
              /<ProductRevision\s+id="([^"]*)"[^>]*>/g,
              (match, id) => {
                if (!match.includes('<UserData')) {
                  return match.replace(/>$/, `>\n  <UserData id="UD-${id}"><UserValue title="last_mod_date" value="${today}T08:00:00"/></UserData>`);
                }
                return match;
              }
            );
          }
        });
      }
    });
  }

  // ============================================
  // 5. RELATION CHECKS
  // ============================================
  _checkRelations() {
    // Check ImanRelation elements
    const relations = (this.xml.match(/<ImanRelation\s/g) || []).length;
    
    if (relations > 0) {
      // Check for missing ClassifiedObjectRef
      const missingObjRef = !this.xml.includes('<ClassifiedObjectRef');
      const missingByRef = !this.xml.includes('<ClassifiedByRef');
      
      if (missingObjRef || missingByRef) {
        this.errors.push({
          id: 'REL-001',
          priority: 'CRITICAL',
          category: 'Relations',
          title: 'Incomplete ImanRelation',
          detail: 'ImanRelation requires both ClassifiedObjectRef and ClassifiedByRef.',
          impact: 'Relations will not be created during import.',
          fix: 'Add both ClassifiedObjectRef and ClassifiedByRef to each ImanRelation.',
          autoFix: false
        });
      }

      // Check for relationType
      const hasRelationType = this.xml.includes('relationType=');
      if (!hasRelationType) {
        this.errors.push({
          id: 'REL-002',
          priority: 'CRITICAL',
          category: 'Relations',
          title: 'Missing relationType on ImanRelation',
          detail: 'ImanRelation requires a relationType attribute.',
          impact: 'Relations will not be created correctly.',
          fix: 'Add relationType attribute to ImanRelation elements.',
          autoFix: false
        });
      }
    }

    // Check for orphaned process occurrences
    const hasProcessOcc = this.xml.includes('<ProcessOccurrence');
    const hasProcess = this.xml.includes('<Process') || this.xml.includes('<ProcessRevision');
    if (hasProcessOcc && !hasProcess) {
      this.warnings.push({
        id: 'REL-003',
        priority: 'HIGH',
        category: 'Relations',
        title: 'ProcessOccurrence without Process',
        detail: 'Found ProcessOccurrence but no Process/ProcessRevision to reference.',
        impact: 'ProcessOccurrences will be orphaned.',
        fix: 'Ensure Process and ProcessRevision exist for each ProcessOccurrence.',
        autoFix: false
      });
    }
  }

  // ============================================
  // 6. WORK PACKAGE CHECKS
  // ============================================
  _checkWorkPackage() {
    if (!this.xml.includes('MECollaborationContext')) return;
    
    const hasEBOMLink = this.xml.includes('WorkPackageEBOM') || 
                        (this.xml.includes('WP-EBOM') && this.xml.includes('EBOM-RR'));
    const hasMBOMLink = this.xml.includes('WorkPackageMBOM') || 
                        (this.xml.includes('WP-MBOM') && this.xml.includes('MBOM-RR'));
    const hasBOPLink = this.xml.includes('WorkPackageBOP') || 
                       (this.xml.includes('WP-BOP') && this.xml.includes('BOP-RR'));
    
    // Get WP ID
    const wpMatch = this.xml.match(/<CollaborationContext\s+id="([^"]+)"/);
    const wpId = wpMatch ? wpMatch[1] : 'unknown';
    
    if (!hasEBOMLink) {
      this.warnings.push({
        id: 'WP-001',
        priority: 'HIGH',
        category: 'Work Package',
        title: 'Missing WP→EBOM Link',
        detail: `Work Package "${wpId}" should be linked to EBOM via ImanRelation.`,
        impact: 'Easy Plan will not show EBOM under this Work Package.',
        fix: 'Add ImanRelation with relationType="WorkPackageEBOM".',
        autoFix: true,
        autoFixFn: () => this._addWPLink('EBOM')
      });
    }
    if (!hasMBOMLink) {
      this.warnings.push({
        id: 'WP-002',
        priority: 'HIGH',
        category: 'Work Package',
        title: 'Missing WP→MBOM Link',
        detail: `Work Package "${wpId}" should be linked to MBOM via ImanRelation.`,
        impact: 'Easy Plan will not show MBOM under this Work Package.',
        fix: 'Add ImanRelation with relationType="WorkPackageMBOM".',
        autoFix: true,
        autoFixFn: () => this._addWPLink('MBOM')
      });
    }
    if (!hasBOPLink) {
      this.warnings.push({
        id: 'WP-003',
        priority: 'HIGH',
        category: 'Work Package',
        title: 'Missing WP→BOP Link',
        detail: `Work Package "${wpId}" should be linked to BOP via ImanRelation.`,
        impact: 'Easy Plan will not show BOP under this Work Package.',
        fix: 'Add ImanRelation with relationType="WorkPackageBOP".',
        autoFix: true,
        autoFixFn: () => this._addWPLink('BOP')
      });
    }
    
    // Check for Work Package description
    if (!this.xml.includes('WP | Plant:')) {
      this.suggestions.push({
        id: 'SUG-WP-001',
        title: 'Add Work Package Description',
        detail: 'Work Package should include plant and owner information in description.',
        action: 'Add description like "WP | Plant:JEDDAH | Owner:Manufacturing Engineering" to CollaborationContext.'
      });
    }
  }

  // ============================================
  // 7. BOP STRUCTURE CHECKS
  // ============================================
  _checkBOPStructure() {
    if (!this.xml.includes('<Operation')) return;

    // Check for ProcessOccurrence hierarchy
    const hasProcessOcc = this.xml.includes('<ProcessOccurrence');
    const hasProductView = this.xml.includes('<ProductView');
    
    if (!hasProcessOcc && !hasProductView) {
      this.warnings.push({
        id: 'BOP-001',
        priority: 'MEDIUM',
        category: 'BOP Structure',
        title: 'Operations without Structure',
        detail: 'Operations exist but no ProcessOccurrence or ProductView elements found.',
        impact: 'Operations will be created but not linked in a process hierarchy.',
        fix: 'Operations will be standalone. Use Easy Plan to organize them into Functional Groups.',
        autoFix: false
      });
    }

    // Check for catalogueId (common error source)
    if (this.xml.includes('catalogueId=')) {
      this.errors.push({
        id: 'BOP-002',
        priority: 'CRITICAL',
        category: 'BOP Structure',
        title: 'catalogueId Detected on Operations',
        detail: 'catalogueId can cause ID uniqueness errors (252106) if IDs already exist.',
        impact: 'Import may FAIL with "ID is not unique" error.',
        fix: 'Remove all catalogueId attributes from Operation elements.',
        autoFix: true,
        autoFixFn: () => {
          this.xml = this.xml.replace(/\s+catalogueId="[^"]*"/g, '');
        }
      });
    }

    // Check for productId (can cause permission issues)
    if (this.xml.includes('productId=')) {
      this.warnings.push({
        id: 'BOP-003',
        priority: 'HIGH',
        category: 'BOP Structure',
        title: 'productId Detected',
        detail: 'productId can cause write access denied errors (515001) if ID already exists.',
        impact: 'Import may fail with access denied errors.',
        fix: 'Remove productId attributes to let TC auto-assign.',
        autoFix: true,
        autoFixFn: () => {
          this.xml = this.xml.replace(/\s+productId="[^"]*"/g, '');
        }
      });
    }

    // Check operation sequence numbers
    const ops = this.xml.match(/<Operation[^>]*>/g) || [];
    ops.forEach(op => {
      if (op.includes('sequence="') || op.includes('seq="')) {
        // Check if sequence is empty
        if (op.includes('sequence=""') || op.includes('seq=""')) {
          this.warnings.push({
            id: 'BOP-004',
            priority: 'MEDIUM',
            category: 'BOP Structure',
            title: 'Empty Sequence Number',
            detail: 'Operation has empty sequence attribute.',
            impact: 'Operations may not order correctly in BOP.',
            fix: 'Provide a sequence number for each operation.',
            autoFix: false
          });
        }
      }
    });

    // Check for missing workCenter on operations
    const opsWithoutWC = this.xml.match(/<Operation[^>]*>/g) || [];
    opsWithoutWC.forEach(op => {
      if (!op.includes('workCenter') && !op.includes('wc') && !op.includes('<UserValue title="workCenter"')) {
        this.warnings.push({
          id: 'BOP-005',
          priority: 'LOW',
          category: 'BOP Structure',
          title: 'Operation without Work Center',
          detail: 'Operation found without workCenter attribute.',
          impact: 'Operations will not be assigned to equipment.',
          fix: 'Add workCenter or equipmentRefs to the operation.',
          autoFix: false
        });
      }
    });
  }

  // ============================================
  // 8. BOE HIERARCHY CHECKS
  // ============================================
  _checkBOEHierarchy() {
    if (!this.xml.includes('<Equipment')) return;
    
    const equipCount = (this.xml.match(/<Equipment\s/g) || []).length;
    const hierarchyCount = (this.xml.match(/<EquipmentHierarchy/g) || []).length;
    const equipWithParent = (this.xml.match(/<Equipment[^>]*parent[^>]*>/g) || []).length;
    
    if (equipCount > 1 && hierarchyCount === 0 && equipWithParent === 0) {
      this.warnings.push({
        id: 'BOE-001',
        priority: 'MEDIUM',
        category: 'Equipment Hierarchy',
        title: 'Equipment without Hierarchy',
        detail: `${equipCount} Equipment elements but no EquipmentHierarchy links or parent attributes.`,
        impact: 'Equipment will be created but not linked in a hierarchy.',
        fix: 'Add EquipmentHierarchy elements or parent attributes.',
        autoFix: false
      });
    }

    // Check equipment levels
    const equipLevels = this.xml.match(/<Equipment[^>]*level="[^"]*"[^>]*>/g) || [];
    const validLevels = ['Site', 'Area', 'ProductionLine', 'ProcessCell', 'WorkUnit', 'StorageZone'];
    equipLevels.forEach(el => {
      const match = el.match(/level="([^"]*)"/);
      if (match && !validLevels.includes(match[1])) {
        this.warnings.push({
          id: 'BOE-002',
          priority: 'MEDIUM',
          category: 'Equipment Hierarchy',
          title: `Invalid Equipment Level: "${match[1]}"`,
          detail: 'Equipment level should be Site, Area, ProductionLine, ProcessCell, WorkUnit, or StorageZone.',
          impact: 'May cause import issues.',
          fix: `Change level to one of: ${validLevels.join(', ')}`,
          autoFix: false
        });
      }
    });
  }

  // ============================================
  // 9. ID UNIQUENESS CHECKS
  // ============================================
  _checkIDsAndUniqueness() {
    // Check for duplicate IDs
    const idMatches = this.xml.match(/id="([^"]+)"/g) || [];
    const ids = idMatches.map(m => m.replace(/id="/, '').replace(/"$/, ''));
    const duplicates = ids.filter((id, index) => ids.indexOf(id) !== index);
    
    if (duplicates.length > 0) {
      const uniqueDups = [...new Set(duplicates)];
      this.errors.push({
        id: 'ID-001',
        priority: 'CRITICAL',
        category: 'IDs',
        title: 'Duplicate IDs Detected',
        detail: `Found ${duplicates.length} duplicate IDs: ${uniqueDups.slice(0, 5).join(', ')}${uniqueDups.length > 5 ? ` and ${uniqueDups.length - 5} more` : ''}`,
        impact: 'Import will FAIL - Teamcenter requires unique IDs.',
        fix: 'Ensure all id attributes are unique within the file.',
        autoFix: true,
        autoFixFn: () => {
          const seen = {};
          let counter = 1;
          this.xml = this.xml.replace(/id="([^"]+)"/g, (match, id) => {
            if (seen[id]) {
              const newId = `${id}-UNIQUE-${counter++}`;
              seen[id] = true;
              return `id="${newId}"`;
            }
            seen[id] = true;
            return match;
          });
        }
      });
    }

    // Check for empty IDs
    if (this.xml.includes('id=""')) {
      this.errors.push({
        id: 'ID-002',
        priority: 'CRITICAL',
        category: 'IDs',
        title: 'Empty ID Attributes',
        detail: 'One or more elements have empty id="" attributes.',
        impact: 'Import will FAIL.',
        fix: 'All id attributes must have non-empty values.',
        autoFix: true,
        autoFixFn: () => {
          let counter = 1;
          this.xml = this.xml.replace(/id=""/g, () => `id="AUTO-ID-${counter++}"`);
        }
      });
    }

    // Check for invalid characters in IDs
    const invalidIdPattern = /id="([^"]*[^a-zA-Z0-9\-_][^"]*)"/g;
    const invalidIds = this.xml.match(invalidIdPattern) || [];
    if (invalidIds.length > 0) {
      this.warnings.push({
        id: 'ID-003',
        priority: 'HIGH',
        category: 'IDs',
        title: 'Invalid Characters in IDs',
        detail: 'IDs should only contain alphanumeric characters, hyphens, and underscores.',
        impact: 'May cause import issues.',
        fix: 'Use only alphanumeric, hyphens, and underscores in IDs.',
        autoFix: true,
        autoFixFn: () => {
          this.xml = this.xml.replace(/id="([^"]+)"/g, (match, id) => {
            const cleanId = id.replace(/[^a-zA-Z0-9\-_]/g, '-');
            if (cleanId !== id) {
              return `id="${cleanId}"`;
            }
            return match;
          });
        }
      });
    }
  }

  // ============================================
  // 10. ATTRIBUTE CHECKS
  // ============================================
  _checkAttributes() {
    // Check for decimal quantities
    const qtyMatches = this.xml.match(/<Quantity\s+value="([^"]+)"/g) || [];
    qtyMatches.forEach(m => {
      const val = parseFloat(m.match(/value="([^"]+)"/)[1]);
      if (!Number.isInteger(val) && val > 0) {
        this.warnings.push({
          id: 'ATTR-001',
          priority: 'HIGH',
          category: 'Attributes',
          title: `Decimal Quantity: ${val}`,
          detail: 'Teamcenter expects integer quantities for BOM occurrences.',
          impact: 'May cause "Specified value is not an integer" error (38009).',
          fix: 'Use whole numbers for quantities. Put fractional amounts in description.',
          autoFix: true,
          autoFixFn: () => {
            this.xml = this.xml.replace(/<Quantity\s+value="([^"]+)"/g, (match, val) => {
              const num = parseFloat(val);
              if (!Number.isInteger(num) && num > 0) {
                return `<Quantity value="${Math.round(num)}"`;
              }
              return match;
            });
          }
        });
      }
    });

    // Check for missing revisions
    const productMatches = this.xml.match(/<Product\s+id="([^"]*)"[^>]*>/g) || [];
    productMatches.forEach(m => {
      const idMatch = m.match(/id="([^"]*)"/);
      if (idMatch) {
        const productId = idMatch[1];
        if (!this.xml.includes(`masterRef="#${productId}"`)) {
          this.warnings.push({
            id: 'ATTR-002',
            priority: 'MEDIUM',
            category: 'Attributes',
            title: `Product without Revision: ${productId}`,
            detail: 'Product element found without a corresponding ProductRevision.',
            impact: 'Product may not be importable without a revision.',
            fix: 'Add a ProductRevision referencing this Product.',
            autoFix: false
          });
        }
      }
    });
  }

  // ============================================
  // 11. BMIDE TYPE CHECKS
  // ============================================
  _checkBMIDETypes() {
    const knownTypes = [
      'Part', 'Part Revision', 'ARA4Part', 'ARA4PartRevision',
      'ARA4MPart', 'ARA4MPartRevision', 'MESPart', 'MESPartRevision',
      'MEProductBOP', 'MEProductBOPRevision', 'MEOP', 'MEOPRevision',
      'Mbc0FuncGroup', 'Mbc0FuncGroupRevision', 'MEProcess', 'MEProcessRevision',
      'MECollaborationContext', 'MESResource', 'MESSkill',
      'MESInspectionPlan', 'MESQualityCharacteristic',
      'MESArea', 'MESWorkUnit', 'MESStorageZone', 'MESProductionLine', 'MESProcessCell',
      'Document', 'MESDocument', 'MESOperation', 'MESOperationRevision'
    ];
    
    const subTypeMatches = this.xml.match(/subType="([^"]+)"/g) || [];
    subTypeMatches.forEach(m => {
      const type = m.replace(/subType="/, '').replace(/"$/, '');
      if (!knownTypes.includes(type) && !type.startsWith('MES') && type !== 'Item' && type !== 'ItemRevision') {
        this.warnings.push({
          id: 'BT-001',
          priority: 'MEDIUM',
          category: 'BMIDE Types',
          title: `Unknown subType: "${type}"`,
          detail: 'This type may not exist in standard Teamcenter 2512 BMIDE.',
          impact: 'May cause "Unknown business object type" error during import.',
          fix: 'Verify this type exists in your BMIDE or use a standard OOTB type.',
          autoFix: false
        });
      }
    });
  }

  // ============================================
  // 12. FORBIDDEN PATTERNS
  // ============================================
  _checkForbiddenPatterns() {
    // Check for FindNumber in UserData (causes 38060)
    if (this.xml.includes('FindNumber') && this.xml.includes('AttributesInContext')) {
      this.errors.push({
        id: 'FP-001',
        priority: 'CRITICAL',
        category: 'Forbidden Patterns',
        title: 'FindNumber in Occurrence UserData',
        detail: 'Setting FindNumber via UserData/AttributesInContext causes "Property is not modifiable" error (38060).',
        impact: 'Import will FAIL for these occurrences.',
        fix: 'Remove FindNumber from UserData. Set it via ProductInstance sequenceNumber instead.',
        autoFix: true,
        autoFixFn: () => {
          this.xml = this.xml.replace(/<UserValue title="FindNumber"\s+value="[^"]*"\/>/g, '');
          this.xml = this.xml.replace(/<UserValue title="Find No\."\s+value="[^"]*"\/>/g, '');
        }
      });
    }

    // Check for OccurrenceName in UserData
    if (this.xml.includes('OccurrenceName') && this.xml.includes('AttributesInContext')) {
      this.errors.push({
        id: 'FP-002',
        priority: 'CRITICAL',
        category: 'Forbidden Patterns',
        title: 'OccurrenceName in Occurrence UserData',
        detail: 'Setting OccurrenceName via UserData/AttributesInContext causes "Property is not modifiable" error (38060).',
        impact: 'Import will FAIL for these occurrences.',
        fix: 'Remove OccurrenceName from UserData.',
        autoFix: true,
        autoFixFn: () => {
          this.xml = this.xml.replace(/<UserValue title="OccurrenceName"\s+value="[^"]*"\/>/g, '');
        }
      });
    }

    // Check for itemName in UserData (legacy issue)
    if (this.xml.includes('itemName') && this.xml.includes('AttributesInContext')) {
      this.warnings.push({
        id: 'FP-003',
        priority: 'HIGH',
        category: 'Forbidden Patterns',
        title: 'itemName in UserData',
        detail: 'Setting itemName via UserData/AttributesInContext may cause issues.',
        impact: 'May cause import errors.',
        fix: 'Remove itemName from UserData. Use Product/name attribute instead.',
        autoFix: true,
        autoFixFn: () => {
          this.xml = this.xml.replace(/<UserValue title="itemName"\s+value="[^"]*"\/>/g, '');
        }
      });
    }
  }

  // ============================================
  // 13. TEAMCENTER 2512 SPECIFIC CHECKS
  // ============================================
  _checkTeamcenter2512Compatibility() {
    // Check for correct MBOM revision attributes
    if (this.xml.includes('ARA4MPart') && !this.xml.includes('ARM') && !this.xml.includes('APM')) {
      this.warnings.push({
        id: 'TC-001',
        priority: 'HIGH',
        category: 'Teamcenter 2512',
        title: 'Missing MBOM Attribute References',
        detail: 'ARA4MPart should reference ADM, APM, and ARM attributes.',
        impact: 'MBOM may not display correctly in Teamcenter.',
        fix: 'Add attributeRefs="ADM APM" to MBOM Product and attributeRefs="ARM" to MBOM Revision.',
        autoFix: false
      });
    }

    // Check for Work Package type
    if (this.xml.includes('MECollaborationContext') && !this.xml.includes('UserData id="UD-WP"')) {
      this.warnings.push({
        id: 'TC-002',
        priority: 'MEDIUM',
        category: 'Teamcenter 2512',
        title: 'Work Package without Properties',
        detail: 'Work Package should have UserData for plant and owner.',
        impact: 'Work Package may not show all properties in Easy Plan.',
        fix: 'Add UserData with plant and owner UserValue elements.',
        autoFix: false
      });
    }

    // Check for Operation Work Center references
    const ops = this.xml.match(/<Operation[^>]*>/g) || [];
    ops.forEach(op => {
      if (op.includes('workCenter') || op.includes('wc')) {
        const wcMatch = op.match(/workCenter="([^"]*)"/) || op.match(/wc="([^"]*)"/);
        if (wcMatch) {
          const wc = wcMatch[1];
          if (!this.xml.includes(`id="${wc}"`) && !this.xml.includes(`name="${wc}"`)) {
            this.warnings.push({
              id: 'TC-003',
              priority: 'LOW',
              category: 'Teamcenter 2512',
              title: `Work Center "${wc}" not found in BOE`,
              detail: 'Operation references a work center that is not defined in BOE.',
              impact: 'Work Center reference will be ignored.',
              fix: 'Add Equipment element for this work center or correct the reference.',
              autoFix: false
            });
          }
        }
      }
    });
  }

  // ============================================
  // 14. GENERATE STATISTICS
  // ============================================
  _generateStats() {
    this.stats.ebom = (this.xml.match(/<Product\s+[^>]*subType="Part"/g) || []).length;
    this.stats.mbom = (this.xml.match(/<Product\s+[^>]*subType="ARA4MPart"/g) || []).length + 
                       (this.xml.match(/<Product\s+[^>]*subType="MESPart"/g) || []).length;
    this.stats.bop = (this.xml.match(/<Product\s+[^>]*subType="MEProductBOP"/g) || []).length;
    this.stats.boe = (this.xml.match(/<Equipment\s/g) || []).length;
    this.stats.wp = (this.xml.match(/MECollaborationContext/g) || []).length;
    this.stats.tools = (this.xml.match(/<Resource\s/g) || []).length;
    this.stats.skills = (this.xml.match(/<Skill\s/g) || []).length;
    this.stats.quality = (this.xml.match(/MESInspectionPlan/g) || []).length + 
                          (this.xml.match(/MESQualityCharacteristic/g) || []).length;
    this.stats.ewi = (this.xml.match(/<Document\s/g) || []).length;
    this.stats.relations = (this.xml.match(/<ImanRelation\s/g) || []).length;
    this.stats.operations = (this.xml.match(/<Operation\s/g) || []).length;
    this.stats.fgs = (this.xml.match(/<Process\s/g) || []).length;
    this.stats.processes = this.stats.fgs;
    this.stats.equipment = this.stats.boe;
    this.stats.documents = this.stats.ewi;
    this.stats.total = this.stats.ebom + this.stats.mbom + this.stats.bop + this.stats.boe + 
                       this.stats.wp + this.stats.tools + this.stats.skills + 
                       this.stats.quality + this.stats.ewi + this.stats.operations + this.stats.fgs;
  }

  // ============================================
  // 15. GENERATE SUGGESTIONS
  // ============================================
  _generateSuggestions() {
    // Suggest adding ImanRelation if WorkPackage exists
    if (this.stats.wp > 0 && this.stats.relations === 0) {
      this.suggestions.push({
        id: 'SUG-001',
        title: 'Add Work Package Relations',
        detail: 'Your Work Package should link to EBOM, MBOM, and BOP via ImanRelation for full Easy Plan integration.',
        action: 'Add ImanRelation with relationType="WorkPackageEBOM", "WorkPackageMBOM", "WorkPackageBOP".'
      });
    }

    // Suggest adding EWI if operations exist
    if (this.stats.operations > 0 && this.stats.ewi === 0) {
      this.suggestions.push({
        id: 'SUG-002',
        title: 'Add EWI Documents',
        detail: 'Operations exist but no EWI (Electronic Work Instructions) documents are included.',
        action: 'Add Document elements linked to operations for shop floor work instructions.'
      });
    }

    // Suggest adding Quality Plans for inspection operations
    const hasInspection = this.xml.includes('Inspection') || this.xml.includes('QC-') || this.xml.includes('inspection');
    if (hasInspection && this.stats.quality === 0) {
      this.suggestions.push({
        id: 'SUG-003',
        title: 'Add Quality Inspection Plans',
        detail: 'Inspection operations detected but no MESInspectionPlan elements.',
        action: 'Add InspectionPlan and QualityCharacteristic elements linked to inspection operations.'
      });
    }

    // Suggest adding Skills if operations exist
    if (this.stats.operations > 0 && this.stats.skills === 0) {
      this.suggestions.push({
        id: 'SUG-004',
        title: 'Add Skills Requirements',
        detail: 'Operations exist but no Skill elements are defined.',
        action: 'Add Skill elements and link them to operations via OperationSkill relations.'
      });
    }

    // Suggest adding Tools if operations exist
    if (this.stats.operations > 0 && this.stats.tools === 0) {
      this.suggestions.push({
        id: 'SUG-005',
        title: 'Add Tools Requirements',
        detail: 'Operations exist but no Tool/Resource elements are defined.',
        action: 'Add Resource elements and link them to operations via OperationTool relations.'
      });
    }
  }

  // ============================================
  // AUTO-FIX FUNCTIONS
  // ============================================
  _addWPLink(type) {
    const wpIdMatch = this.xml.match(/<CollaborationContext\s+id="([^"]+)"/);
    if (!wpIdMatch) return;
    const wpId = wpIdMatch[1];
    const revIds = { EBOM: 'EBOM-RR', MBOM: 'MBOM-RR', BOP: 'BOP-RR' };
    const revId = revIds[type];
    if (!revId || !this.xml.includes(revId)) return;
    
    const relation = `\n <ImanRelation id="WP-${type}-AUTO" subType="ImanRelation" accessRefs="#A-AUTO" relationType="WorkPackage${type}"><Description>Auto-Fixed Work Package → ${type} Link</Description><ClassifiedObjectRef idref="${wpId}"/><ClassifiedByRef idref="#${revId}"/></ImanRelation>`;
    
    if (this.xml.includes('</PLMXML>')) {
      this.xml = this.xml.replace('</PLMXML>', `${relation}\n</PLMXML>`);
    }
  }

  // ============================================
  // GET RESULTS
  // ============================================
  getResults() {
    if (!this.validated) {
      this.validate();
    }
    return {
      errors: this.errors,
      warnings: this.warnings,
      suggestions: this.suggestions,
      stats: this.stats,
      totalIssues: this.errors.length + this.warnings.length,
      criticalCount: this.errors.filter(e => e.priority === 'CRITICAL').length,
      highCount: this.errors.filter(e => e.priority === 'HIGH').length + this.warnings.filter(w => w.priority === 'HIGH').length,
      mediumCount: this.warnings.filter(w => w.priority === 'MEDIUM').length,
      isImportReady: this.errors.length === 0,
      autoFixableCount: [...this.errors, ...this.warnings].filter(i => i.autoFix).length
    };
  }

  // ============================================
  // APPLY ALL AUTO-FIXES
  // ============================================
  applyAllAutoFixes() {
    this.validate();
    const allIssues = [...this.errors, ...this.warnings];
    const fixable = allIssues.filter(i => i.autoFix && i.autoFixFn);
    fixable.forEach(i => i.autoFixFn());
    this.correctionsApplied = fixable.map(i => i.id);
    
    // Re-validate after fixes
    this.errors = [];
    this.warnings = [];
    this.suggestions = [];
    this.validated = false;
    this.validate();
    
    return this;
  }

  // ============================================
  // GET CORRECTED XML
  // ============================================
  getCorrectedXML() {
    return this.xml;
  }

  // ============================================
  // GET CORRECTION SUMMARY
  // ============================================
  getCorrectionSummary() {
    return {
      applied: this.correctionsApplied.length,
      fixes: this.correctionsApplied
    };
  }
}

// ============================================
// VALIDATE PLMXML COMPONENT
// ============================================
const ValidatePLMXML = ({ plmxmlContent, onApplyCorrections, onClose }) => {
  const [results, setResults] = useState(null);
  const [validating, setValidating] = useState(false);
  const [autoFixing, setAutoFixing] = useState(false);
  const [showCorrected, setShowCorrected] = useState(false);
  const [correctedXML, setCorrectedXML] = useState('');
  const [expandedIssues, setExpandedIssues] = useState({});
  const [filter, setFilter] = useState('all');
  const [hoveredIssue, setHoveredIssue] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const [showFullXML, setShowFullXML] = useState(false);

  const validatorRef = useRef(null);

  const runValidation = useCallback(() => {
    setValidating(true);
    setTimeout(() => {
      validatorRef.current = new PLMXMLValidator(plmxmlContent);
      validatorRef.current.validate();
      setResults(validatorRef.current.getResults());
      setValidating(false);
    }, 300);
  }, [plmxmlContent]);

  const runAutoFix = useCallback(() => {
    if (!validatorRef.current) return;
    setAutoFixing(true);
    setTimeout(() => {
      validatorRef.current.applyAllAutoFixes();
      setResults(validatorRef.current.getResults());
      setCorrectedXML(validatorRef.current.getCorrectedXML());
      setShowCorrected(true);
      setAutoFixing(false);
      // Notify parent
      if (onApplyCorrections) {
        onApplyCorrections(validatorRef.current.getCorrectedXML());
      }
    }, 500);
  }, [onApplyCorrections]);

  const downloadCorrected = useCallback(() => {
    if (!correctedXML) return;
    saveAs(new Blob([correctedXML], { type: 'application/xml;charset=utf-8' }), 'PLMXML_Corrected_Import.plmxml');
  }, [correctedXML]);

  const toggleIssue = (id) => {
    setExpandedIssues(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const handleMouseEnter = (e, issue) => {
    setHoveredIssue(issue.id);
    setTooltipPos({ x: e.clientX + 10, y: e.clientY - 10 });
  };

  const handleMouseLeave = () => setHoveredIssue(null);

  // Run validation on mount
  React.useEffect(() => {
    runValidation();
  }, [runValidation]);

  const filteredIssues = useMemo(() => {
    if (!results) return [];
    const all = [...(results.errors || []), ...(results.warnings || [])];
    if (filter === 'critical') return all.filter(i => i.priority === 'CRITICAL');
    if (filter === 'high') return all.filter(i => i.priority === 'HIGH');
    if (filter === 'medium') return all.filter(i => i.priority === 'MEDIUM');
    if (filter === 'autofix') return all.filter(i => i.autoFix);
    if (filter === 'errors') return results.errors || [];
    return all;
  }, [results, filter]);

  const priorityColors = {
    CRITICAL: { bg: `${T.d}15`, border: T.d, icon: <AlertOctagon size={12} color={T.d} />, label: 'CRITICAL', color: T.d },
    HIGH: { bg: `${T.w}15`, border: T.w, icon: <AlertTriangle size={12} color={T.w} />, label: 'HIGH', color: T.w },
    MEDIUM: { bg: `${T.pl}15`, border: T.pl, icon: <Info size={12} color={T.pl} />, label: 'MEDIUM', color: T.pl },
    LOW: { bg: `${T.t3}15`, border: T.t3, icon: <Info size={12} color={T.t3} />, label: 'LOW', color: T.t3 },
  };

  if (validating) {
    return (
      <div style={S.container}>
        <div style={{ textAlign: 'center', padding: '30px' }}>
          <RefreshCw size={32} color={T.p} style={{ animation: 'spin 1s linear infinite', marginBottom: '12px' }} />
          <div style={{ color: T.t2, fontSize: '0.9rem' }}>Validating PLMXML structure...</div>
          <div style={{ color: T.t3, fontSize: '0.7rem', marginTop: '4px' }}>Checking schema, business objects, relations, and known error patterns</div>
          <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    );
  }

  if (!results) {
    return (
      <div style={S.container}>
        <div style={{ textAlign: 'center', padding: '30px', color: T.t3 }}>No validation results. Click "Validate" to start.</div>
        <button onClick={runValidation} style={{ ...S.btn, ...S.bPri, margin: '0 auto' }}><Search size={14} /> Validate</button>
      </div>
    );
  }

  return (
    <div style={S.container}>
      {/* HEADER */}
      <div style={S.header}>
        <div style={S.title}>
          <Shield size={16} color={T.ppl} />
          PLMXML Validator & Auto-Fix Engine
          <span style={{ ...S.badge(T.pp), marginLeft: '8px', fontSize: '0.6rem' }}>500+ Projects Experience</span>
        </div>
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
          {onClose && <button onClick={onClose} style={{ ...S.btn, ...S.bGho }}><X size={12} />Close</button>}
          <button onClick={runValidation} disabled={validating} style={{ ...S.btn, ...S.bPri }}>
            {validating ? <RefreshCw size={12} className="spin" /> : <Search size={12} />}
            {validating ? 'Validating...' : 'Re-validate'}
          </button>
          {!results.isImportReady && (
            <button onClick={runAutoFix} disabled={autoFixing} style={{ ...S.btn, ...S.bWar }}>
              {autoFixing ? <RefreshCw size={12} className="spin" /> : <Wrench size={12} />}
              {autoFixing ? 'Auto-Fixing...' : `Auto-Fix (${results.autoFixableCount})`}
            </button>
          )}
          {correctedXML && (
            <>
              <button onClick={downloadCorrected} style={{ ...S.btn, ...S.bSuc }}>
                <Download size={12} /> Download Corrected
              </button>
              <button onClick={() => setShowFullXML(!showFullXML)} style={{ ...S.btn, ...S.bGho }}>
                {showFullXML ? <EyeOff size={12} /> : <Eye size={12} />}
                {showFullXML ? 'Hide XML' : 'Show XML'}
              </button>
            </>
          )}
        </div>
      </div>

      {/* RESULTS SUMMARY */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', gap: '6px', marginBottom: '10px' }}>
        <div style={S.statCard}>
          <div style={{ fontSize: '1.2rem', fontWeight: 700, color: results.isImportReady ? T.g : T.d }}>
            {results.isImportReady ? '✅' : '❌'}
          </div>
          <div style={{ fontSize: '0.58rem', color: T.t3 }}>{results.isImportReady ? 'Ready to Import' : 'Has Issues'}</div>
        </div>
        <div style={S.statCard}>
          <div style={{ fontSize: '1.2rem', fontWeight: 700, color: T.d }}>{results.criticalCount}</div>
          <div style={{ fontSize: '0.58rem', color: T.t3 }}>Critical</div>
        </div>
        <div style={S.statCard}>
          <div style={{ fontSize: '1.2rem', fontWeight: 700, color: T.w }}>{results.highCount}</div>
          <div style={{ fontSize: '0.58rem', color: T.t3 }}>High</div>
        </div>
        <div style={S.statCard}>
          <div style={{ fontSize: '1.2rem', fontWeight: 700, color: T.pl }}>{results.mediumCount}</div>
          <div style={{ fontSize: '0.58rem', color: T.t3 }}>Medium</div>
        </div>
        <div style={S.statCard}>
          <div style={{ fontSize: '1.2rem', fontWeight: 700, color: T.pp }}>{results.autoFixableCount}</div>
          <div style={{ fontSize: '0.58rem', color: T.t3 }}>Auto-Fixable</div>
        </div>
        <div style={S.statCard}>
          <div style={{ fontSize: '1.2rem', fontWeight: 700, color: T.g }}>{results.stats.total || 0}</div>
          <div style={{ fontSize: '0.58rem', color: T.t3 }}>Total Objects</div>
        </div>
      </div>

      {/* OBJECT BREAKDOWN */}
      <div style={{ ...S.card, marginBottom: '8px' }}>
        <div style={{ fontSize: '0.65rem', fontWeight: 600, color: T.t2, marginBottom: '4px' }}>Object Breakdown</div>
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
          {Object.entries(results.stats).filter(([k]) => !['total', 'processes', 'equipment', 'documents'].includes(k)).map(([k, v]) => v > 0 && (
            <span key={k} style={{ ...S.badge(T.cy), padding: '2px 5px', fontSize: '0.58rem' }}>
              {k.toUpperCase()}: {v}
            </span>
          ))}
        </div>
        {results.stats.operations > 0 && (
          <div style={{ marginTop: '4px' }}>
            <span style={{ ...S.badge(T.or), padding: '2px 5px', fontSize: '0.58rem' }}>Operations: {results.stats.operations}</span>
            <span style={{ ...S.badge(T.w), padding: '2px 5px', fontSize: '0.58rem', marginLeft: '4px' }}>Groups: {results.stats.fgs}</span>
            <span style={{ ...S.badge(T.pp), padding: '2px 5px', fontSize: '0.58rem', marginLeft: '4px' }}>Relations: {results.stats.relations}</span>
          </div>
        )}
      </div>

      {/* FILTER BAR */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '8px', flexWrap: 'wrap' }}>
        {[
          { id: 'all', label: `All (${results.totalIssues})` },
          { id: 'critical', label: `Critical (${results.criticalCount})`, color: T.d },
          { id: 'high', label: `High (${results.highCount})`, color: T.w },
          { id: 'medium', label: `Medium (${results.mediumCount})`, color: T.pl },
          { id: 'autofix', label: `Auto-Fixable (${results.autoFixableCount})`, color: T.pp },
          { id: 'errors', label: `Errors Only (${results.errors?.length || 0})`, color: T.d },
        ].map(f => (
          <button key={f.id} onClick={() => setFilter(f.id)} style={{
            ...S.btn, ...S.bGho, padding: '2px 7px', fontSize: '0.6rem',
            background: filter === f.id ? (f.color || T.p) + '22' : 'transparent',
            borderColor: filter === f.id ? (f.color || T.p) : T.b
          }}>{f.label}</button>
        ))}
      </div>

      {/* ISSUES LIST */}
      <div style={{ maxHeight: '350px', overflowY: 'auto' }}>
        {filteredIssues.length === 0 && (
          <div style={{ textAlign: 'center', padding: '20px', color: T.g, fontSize: '0.8rem' }}>
            <CheckCircle2 size={24} style={{ marginBottom: '6px' }} />
            <div>No issues found for this filter!</div>
          </div>
        )}

        {filteredIssues.map((issue, idx) => {
          const pc = priorityColors[issue.priority] || priorityColors.MEDIUM;
          const isExpanded = expandedIssues[issue.id];
          
          return (
            <div key={issue.id || idx}
              onMouseEnter={(e) => handleMouseEnter(e, issue)}
              onMouseLeave={handleMouseLeave}
              style={{
                ...S.issueRow,
                background: isExpanded ? pc.bg : 'transparent',
                borderLeft: `3px solid ${pc.border}`,
                cursor: 'pointer'
              }}
              onClick={() => toggleIssue(issue.id)}
            >
              <div style={{ flexShrink: 0, marginTop: '2px' }}>{pc.icon}</div>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '2px', flexWrap: 'wrap' }}>
                  <span style={{ ...S.badge(pc.color), fontSize: '0.5rem' }}>{pc.label}</span>
                  <span style={{ ...S.badge(T.t3), fontSize: '0.5rem' }}>{issue.category}</span>
                  <span style={{ fontWeight: 600, fontSize: '0.7rem', color: T.t }}>{issue.title}</span>
                  {issue.autoFix && <span style={{ ...S.badge(T.pp), fontSize: '0.5rem', marginLeft: 'auto' }}>🔧 Auto-Fix</span>}
                </div>
                {isExpanded && (
                  <div style={{ marginTop: '4px', fontSize: '0.62rem', color: T.t2, lineHeight: '1.4' }}>
                    <div><strong>Detail:</strong> {issue.detail}</div>
                    <div><strong>Impact:</strong> <span style={{ color: T.wl }}>{issue.impact}</span></div>
                    <div><strong>Fix:</strong> <span style={{ color: T.gl }}>{issue.fix}</span></div>
                    {issue.autoFix && <div style={{ color: T.ppl, marginTop: '2px' }}>✨ This issue will be automatically fixed by the Auto-Fix engine.</div>}
                  </div>
                )}
              </div>
              <div style={{ flexShrink: 0, color: T.t3, fontSize: '0.6rem' }}>
                {isExpanded ? <ChevronDown size={10} /> : <ChevronRight size={10} />}
              </div>
            </div>
          );
        })}
      </div>

      {/* SUGGESTIONS */}
      {results.suggestions && results.suggestions.length > 0 && (
        <div style={{ ...S.card, marginTop: '8px', border: `1px solid ${T.pp}33` }}>
          <div style={{ fontSize: '0.7rem', fontWeight: 600, color: T.ppl, marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
            <Sparkles size={12} /> Expert Suggestions
          </div>
          {results.suggestions.map((sug, idx) => (
            <div key={idx} style={{ fontSize: '0.62rem', color: T.t2, marginBottom: '4px', padding: '4px 6px', background: T.s, borderRadius: '4px' }}>
              <div style={{ fontWeight: 600, color: T.t }}>{sug.title}</div>
              <div>{sug.detail}</div>
              <div style={{ color: T.gl, marginTop: '2px' }}>💡 {sug.action}</div>
            </div>
          ))}
        </div>
      )}

      {/* CORRECTED XML PREVIEW */}
      {showCorrected && correctedXML && (
        <div style={{ ...S.card, marginTop: '8px', border: `1px solid ${T.g}44` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
            <div style={{ fontSize: '0.7rem', fontWeight: 600, color: T.gl, display: 'flex', alignItems: 'center', gap: '4px' }}>
              <CheckCircle2 size={12} /> Corrected PLMXML
            </div>
            <div style={{ display: 'flex', gap: '4px' }}>
              <button onClick={downloadCorrected} style={{ ...S.btn, ...S.bSuc, padding: '3px 7px', fontSize: '0.62rem' }}>
                <Download size={10} /> Download
              </button>
              <button onClick={() => navigator.clipboard.writeText(correctedXML)} style={{ ...S.btn, ...S.bGho, padding: '3px 7px', fontSize: '0.62rem' }}>
                <Copy size={10} /> Copy
              </button>
            </div>
          </div>
          {showFullXML && <pre style={S.codeBlock}>{correctedXML}</pre>}
          {!showFullXML && (
            <pre style={S.codeBlock}>{correctedXML.substring(0, 1500)}{correctedXML.length > 1500 ? '\n... (truncated, click "Show XML" to view full)' : ''}</pre>
          )}
        </div>
      )}

      {/* HOVER TOOLTIP */}
      {hoveredIssue && (
        <div style={{ ...S.tooltip, position: 'fixed', left: tooltipPos.x, top: tooltipPos.y }}>
          Click to expand for full details and fix instructions
        </div>
      )}
    </div>
  );
};

export default ValidatePLMXML;